/**
 * Утилиты для определения доступных действий с заказами на основе их статуса
 */

export interface OrderAction {
  id: string
  label: string
  icon: string
  variant: 'primary' | 'secondary' | 'danger' | 'success' | 'warning'
  handler: string // Название обработчика в родительском компоненте
}

/**
 * Определяет доступные действия для заказа на основе его статуса
 */
export function getAvailableOrderActions(status: string): OrderAction[] {
  switch (status) {
    case 'new':
      // Новый заказ - можно только просмотреть (автоматически переходит в watching)
      return []

    case 'watching':
      // На просмотре - можно взять в просчет или отказаться
      return [
        {
          id: 'take',
          label: 'Взять в просчет',
          icon: '⏳',
          variant: 'primary',
          handler: 'onTake'
        },
        {
          id: 'reject',
          label: 'Отказаться',
          icon: '❌',
          variant: 'danger',
          handler: 'onReject'
        }
      ]

    case 'calculating':
      // На просчете - можно отправить цену или отказаться
      return [
        {
          id: 'submitPrice',
          label: 'Отправить цену',
          icon: '💰',
          variant: 'success',
          handler: 'onSubmitPrice'
        },
        {
          id: 'reject',
          label: 'Отказаться',
          icon: '❌',
          variant: 'danger',
          handler: 'onReject'
        }
      ]

    case 'price_given':
      // Цена отправлена - можно изменить цену или отказаться
      return [
        {
          id: 'editPrice',
          label: 'Изменить цену',
          icon: '✏️',
          variant: 'warning',
          handler: 'onEditPrice'
        },
        {
          id: 'reject',
          label: 'Отказаться',
          icon: '❌',
          variant: 'danger',
          handler: 'onReject'
        }
      ]

    case 'pending_confirmation':
      // Ожидает подтверждения - никаких действий не доступно
      return []

    case 'executing':
      // В работе - никаких действий не доступно
      return []

    case 'defect':
      // Брак - никаких действий не доступно
      return []

    case 'rejected':
      // Отклонен - можно восстановить
      return [
        {
          id: 'restore',
          label: 'Восстановить',
          icon: '🔄',
          variant: 'success',
          handler: 'onRestore'
        }
      ]

    case 'completed':
    case 'archived':
      // Завершенные статусы - нет доступных действий
      return []

    default:
      // Неизвестный статус - показываем базовые действия
      return [
        {
          id: 'reject',
          label: 'Отказаться',
          icon: '❌',
          variant: 'danger',
          handler: 'onReject'
        }
      ]
  }
}

/**
 * Получает CSS классы для кнопки в зависимости от варианта
 */
export function getActionButtonClasses(variant: OrderAction['variant'], isMobile: boolean = false): string {
  const baseClasses = isMobile 
    ? 'w-full font-medium py-2 px-3 rounded-full shadow-sm transition-all duration-200 active:scale-95'
    : 'w-full font-medium py-2 px-3 rounded-lg shadow-sm transition-all duration-200 hover:shadow-md active:scale-95'

  switch (variant) {
    case 'primary':
      return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700`
    case 'success':
      return `${baseClasses} bg-green-600 text-white hover:bg-green-700`
    case 'danger':
      return `${baseClasses} bg-red-600 text-white hover:bg-red-700`
    case 'warning':
      return `${baseClasses} bg-amber-500 text-white hover:bg-amber-600`
    case 'secondary':
      return `${baseClasses} bg-gray-600 text-white hover:bg-gray-700`
    default:
      return `${baseClasses} bg-gray-600 text-white hover:bg-gray-700`
  }
}

/**
 * Проверяет, доступно ли действие для текущего статуса
 */
export function isActionAvailable(actionId: string, status: string): boolean {
  const availableActions = getAvailableOrderActions(status)
  return availableActions.some(action => action.id === actionId)
}
